import { atom } from 'jotai';
import { Product } from '../Models/Product';
import apiClient, { InkyApiError, InkyApiGeoRestrictedError } from '../API/InkyAPI';
import { Series } from '../Models/Series';
import dashboard from '../API/DashboardAPI';
import { DashboardSeries } from '../Models/DashboardSeries';
import Log from '../Kodansha/Services/Logger';
import { selectAtom } from 'jotai/utils';
import { LoginStatus } from './AccountStore';
import { ThumbnailsFlags } from '../FeatureFlags/ThumbnailsContext';
import { AssetLinkGroup } from '../Models/AssetLinkGroup';

export const SeriesArrayAtom = atom<Series[]>([]);
export const FetchSeriesAtom = atom(
    (get) => {
        return get(SeriesArrayAtom);
    },
    async (get, set, id: number) => {
        const mutate = get(SeriesArrayAtom);
        if(!mutate[id]){
            try {
                const res = await apiClient.getSeries(id.toString(), ThumbnailsFlags.seriesPageWidths, null);
                mutate[id] = res;
                set(SeriesArrayAtom, mutate);
            } catch (e) {
                if(e instanceof InkyApiGeoRestrictedError)
                {
                    switch (e.geoRestrictionStatus) {
                        case "RestrictedWithData":
                        case "RestrictedButOwned":
                        case 'NotRestricted':
                        case "RestrictedButAdmin":
                            const response = e.payload;
                            const series = Object.assign(new Series(), {
                                ...response,
                                assetLinkGroups: response.assetLinkGroups.map(AssetLinkGroup.createFromApi),
                            });
                            mutate[id] = series;
                            set(SeriesArrayAtom, mutate);
                            break;
                        case 'Restricted':
                            break;
                    }
                    return;
                }
                console.error("[SeriesStore1] Could not fetch Product-data: " + e.message, e);
                throw e;
            }
        } else {
            // console.log("[SeriesStore]  Series already fetched.");
        }


    }
);

export const ProductArrayAtom = atom<Product[][]>([]);
export const FetchProductsAtom = atom(
    (get) => {
        return get(ProductArrayAtom);
    },
    async (get, set, id: number) => {
        const mutate = get(ProductArrayAtom);
        if(!mutate[id]){
            try {
                const res = await apiClient.getProductsForSeries(id, null);
                mutate[id] = res;
                set(ProductArrayAtom, mutate);
            } catch (e) {
                console.error("[SeriesStore2] Could not fetch Product-data: " + e.message, e);
                throw e;
            }
        } else {
            // console.log("[SeriesStore]  Series already fetched.");
        }


    }
);


export const AllSeriesArrayAtom = atom<DashboardSeries[]>([]);

interface FetchSeriesFilter {
    query: string;
    publishStatus?: string;
}
export const FetchSeriesArrayAtom = atom(
    (get) => {
        return get(AllSeriesArrayAtom);
    }, async (get, set, filter: FetchSeriesFilter ) => {
        try {
            dashboard.getSeries(null, filter?.query, 10000, filter?.publishStatus).then(res => {
                set(AllSeriesArrayAtom, res.data);
            });
        } catch (e){
            console.error('[SeriesStore] could not get series', e);
            throw e;
        }
    });

FetchSeriesArrayAtom.onMount = (setAtom): void => {
    setAtom(null).then(r => {
        Log.debug("FetchSeriesArrayAtom mounted.");
    });
}


/**
 * For a single Series on the Series page.
 */
export const SingleSeriesAtom = atom<Series>(null as Series);
SingleSeriesAtom.onMount = (setAtom) => {
    //Get rid of the product when we close the Editor (unmount).
    Log.debug("Mounting series...");
    return () => {
        Log.debug("Unmounting series...");
        // setAtom(null);
    }
}

const LoadingSingleSeriesAtom = atom<boolean>(false);

/**
 * Products for the series (derived)
 */
export const ProductsForSeriesAtom = selectAtom(SingleSeriesAtom, series => series?.products);
/**
 * Does this Series have any chapters?
 */
export const SingleSeriesHasChaptersAtom = selectAtom(SingleSeriesAtom, series => series?.products.some(x => x.hasChapters()));

/**
 * Was the series not found?
 */
export const SingleSeriesErrorAtom = atom<boolean|"NotFound"|"GeoRestricted">(false);
SingleSeriesErrorAtom.onMount = (setAtom) => {
    //Get rid of the product when we close the Editor (unmount).
    return () => {
        setAtom(false);
    }
}

export const LoginStatusForSeriesAtom = atom<LoginStatus>("Unknown");


/**
 * Fetch the series.
 */
export const FetchSingleSeriesAtom = atom(
    (get) => {
        const s = get(SingleSeriesAtom);
        if(s?.products) {
            return s;
        }

        return null;
    },
    async (get, set, seriesIdOrText: string ) => {
        if(get(LoadingSingleSeriesAtom)) {
            //IN the middle of loading this. Ignore.
            Log.warn("Attempting to double load this series. Ignore!");
            return;
        }
        try {
            set(LoadingSingleSeriesAtom, true);
            const response = await apiClient.getSeries(seriesIdOrText, ThumbnailsFlags.seriesPageWidths, null);
            const series = Object.assign(new Series(), { ...response, assetLinkGroups: response.assetLinkGroups.map(AssetLinkGroup.createFromApi) });
            set(SingleSeriesAtom, series);

            const products = await apiClient.getProductsForSeries(response.id, null);

            set(SingleSeriesAtom, series.attachProducts(products));
            Log.info("Series loaded.")
        }
        catch (e) {
            if( e instanceof  InkyApiGeoRestrictedError){
                //load data
                switch (e.geoRestrictionStatus) {
                    case "RestrictedWithData":
                    case "RestrictedButOwned":
                    case 'NotRestricted':
                    case "RestrictedButAdmin":
                        const response = e.payload;
                        const series = Object.assign(new Series(), {
                            ...response,
                            assetLinkGroups: response.assetLinkGroups.map(AssetLinkGroup.createFromApi),
                        });
                        set(SingleSeriesAtom, series);
                        const products = await apiClient.getProductsForSeries(response.id, null);
                        set(SingleSeriesAtom, series.attachProducts(products));
                        Log.info('Series loaded.');

                        break;
                    case 'Restricted':
                        break;
                }

                //set errors
                switch (e.geoRestrictionStatus) {
                    case "RestrictedWithData":
                    case "RestrictedButOwned":
                    case "Restricted":
                        set(SingleSeriesErrorAtom, "GeoRestricted");
                        Log.error("Series GeoRestricted", e);
                    case 'NotRestricted':
                    case 'RestrictedButAdmin':
                        break;
                }
            } else if(e instanceof  InkyApiError){
                switch (e.status) {
                    case "NotFound":
                        set(SingleSeriesErrorAtom, "NotFound");
                        Log.error("Series Not Found", e);
                        break;
                    default:
                        Log.error("Series Error", e);
                        set(SingleSeriesErrorAtom, "NotFound");

                }
            }
        }
        finally {
            set(LoadingSingleSeriesAtom, false);
        }
    }
)


